import React, { useState, useEffect, useRef } from 'react';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import axiosPost from '../../../helpers/axios-helper';
import axios from 'axios';
import config from '../../../constants/config';
import MapPicker from '../../../components/MapPicker';

export default function Hotels() {
  // Common amenities users can select
  const DEFAULT_AMENITIES = [
    'Free WiFi',
    'Pool',
    'Gym',
    'Spa',
    'Restaurant',
    'Bar',
    'Room Service',
    'Air Conditioning',
    'Airport Shuttle',
    'Parking',
    'Pet Friendly',
    'Non-smoking Rooms',
    'Beachfront',
    '24-hour Front Desk',
    'Business Center',
    'Laundry',
  ];
  // Default Extras sections to auto-create per hotel when none exist
  const DEFAULT_EXTRAS = [
    'The Magic',
    'Mood',
    'Origins',
    'The Rooms',
    'Flavours',
    'Wellness',
    'For Families',
    'The Service',
    'Sustainability',
    'Accessibility',
    'LGBTQ+ efforts'
  ];
  const [hotels, setHotels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    type: 'hotel',
    title: '',
    country_id: '',
    country: '',
    location: '',
    image: '',
    images: '',
    active: 1,
    queer_owned: 0,
    contact: {
      address: '',
      phone: '',
      email: '',
      website: ''
    },
    amenities: '',
    content: '',
    map_location: null
  });
  
  // Countries for dropdown (fetched from API)
  const [countries, setCountries] = useState([]);
  // Optional list filters
  const [filters, setFilters] = useState({ country_id: '', active: '' });
  // Extras state for selected hotel
  const [extras, setExtras] = useState([]);
  const [extraForm, setExtraForm] = useState({ id: 0, section_key: '', title: '', description: '', sort_order: 1, active: 1 });
  const extrasFormRef = useRef(null);
  const extrasSectionKeyRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [seedingExtras, setSeedingExtras] = useState(false);

  // Amenities selection state
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [customAmenity, setCustomAmenity] = useState('');

  // Uploading state for images
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  const parseAmenitiesString = (val) => {
    if (!val) return [];
    // If already an array, normalize and return
    if (Array.isArray(val)) {
      return val.map(s => (s || '').toString().trim()).filter(Boolean);
    }
    // Coerce other types to string and split
    const str = typeof val === 'string' ? val : String(val);
    return str
      .split(/\r?\n|,/) // support newline or comma
      .map(s => (s || '').trim())
      .filter(Boolean);
  };

  const parseImagesString = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) {
      return val.map(s => (s || '').toString().trim()).filter(Boolean);
    }
    const str = typeof val === 'string' ? val : String(val);
    return str
      .split(/\r?\n|,/) // support newline or comma
      .map(s => (s || '').trim())
      .filter(Boolean);
  };

  const setImagesFromArray = (arr) => {
    const cleaned = (arr || []).map(s => (s || '').toString().trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, images: cleaned.join('\n') }));
  };

  // Helper to load hotels list
  const loadHotels = (controller, overrides = {}) => {
    const payload = { page: 1, per_page: 50 };
    const countryId = overrides.country_id ?? filters.country_id;
    const activeVal = overrides.active ?? filters.active;
    if (countryId) payload.country_id = parseInt(countryId);
    if (activeVal !== '' && activeVal !== undefined && activeVal !== null) payload.active = parseInt(activeVal);
    axiosPost(
      'hotels/list',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success && resp?.data?.items) {
          setHotels(resp.data.items);
        } else {
          setHotels([]);
        }
        setIsLoading(false);
      },
      controller
    );
  }

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    loadHotels(controller);
    // Fetch countries list for dropdown
    axiosPost(
      'dashboard/countries/list',
      {},
      {},
      (resp) => {
        const list = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
        setCountries(list);
      },
      controller
    );

    return () => controller.abort();
  }, []);

  // Helper: convert display name to a safe section key
  const toSectionKey = (name) => {
    try {
      return (name || '')
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\+/g, ' plus ')
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/_{2,}/g, '_');
    } catch { return '' }
  };

  // Seed default extras for a hotel (runs once when none exist)
  const seedDefaultExtras = async (hotelId) => {
    if (!hotelId) return;
    setSeedingExtras(true);
    const postUpsert = (payload) => new Promise((resolve) => {
      axiosPost(
        'hotels/extras/upsert',
        { 'Content-Type': 'application/json' },
        payload,
        () => resolve()
      );
    });
    try {
      let order = 1;
      for (const name of DEFAULT_EXTRAS) {
        const payload = {
          hotel_id: hotelId,
          section_key: toSectionKey(name),
          title: name,
          description: '',
          sort_order: order++,
          active: 1
        };
        await postUpsert(payload);
      }
    } finally {
      setSeedingExtras(false);
      // Refresh list after seeding
      fetchExtras(hotelId);
    }
  };

  const fetchExtras = (hotelId) => {
    if (!hotelId) { setExtras([]); return; }
    axiosPost(
      'hotels/extras/list',
      { 'Content-Type': 'application/json' },
      { hotel_id: hotelId },
      (resp) => {
        const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
        setExtras(items);
        // Auto-seed default extras if none exist
        if (items.length === 0 && !seedingExtras) {
          seedDefaultExtras(hotelId);
        }
      }
    );
  }

  const handleEdit = (hotel) => {
    setSelectedHotel(hotel);
    // Parse location JSON if it exists
    let mapLocation = null;
    try {
      if (hotel.location && typeof hotel.location === 'string' && hotel.location.trim().startsWith('{')) {
        mapLocation = JSON.parse(hotel.location);
      }
    } catch (e) {
      console.warn('Failed to parse location JSON', e);
    }
    // Ensure defaults for missing new fields
    setFormData({
      ...hotel,
      country_id: hotel.country_id ?? '',
      location: hotel.location ?? '',
      image: hotel.image ?? '',
      images: hotel.images ?? '',
      active: hotel.active ?? 1,
      queer_owned: hotel.queer_owned ?? 0,
      amenities: hotel.amenities || '',
      map_location: mapLocation
    });
    setSelectedAmenities(parseAmenitiesString(hotel.amenities || ''));
    setCustomAmenity('');
    setIsEditing(true);
    fetchExtras(hotel.id);
    // Fetch full hotel details to prefill amenities if not present in list item
    axiosPost(
      'hotels/get',
      { 'Content-Type': 'application/json' },
      { id: hotel.id },
      (resp) => {
        const am = resp?.data?.hotel?.amenities || '';
        setFormData((prev) => ({ ...prev, amenities: am }));
        setSelectedAmenities(parseAmenitiesString(am));
        const ims = resp?.data?.hotel?.images || '';
        setFormData((prev) => ({ ...prev, images: ims }));
      }
    );
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this hotel?')) {
      setHotels(hotels.filter(hotel => hotel.id !== id));
    }
  };

  // Amenities handlers
  const toggleAmenity = (amen) => {
    setSelectedAmenities((prev) => {
      const exists = prev.includes(amen);
      const next = exists ? prev.filter(a => a !== amen) : [...prev, amen];
      return next;
    });
  };

  const addCustomAmenity = () => {
    const val = (customAmenity || '').trim();
    if (!val) return;
    setSelectedAmenities((prev) => prev.includes(val) ? prev : [...prev, val]);
    setCustomAmenity('');
  };

  const removeAmenity = (amen) => {
    setSelectedAmenities((prev) => prev.filter(a => a !== amen));
  };

  const handleAddNew = () => {
    setSelectedHotel(null);
    setFormData({
      type: 'hotel',
      title: '',
      country_id: '',
      country: '',
      location: '',
      image: '',
      images: '',
      active: 1,
      queer_owned: 0,
      contact: {
        address: '',
        phone: '',
        email: '',
        website: ''
      },
      amenities: '',
      content: '',
      map_location: null
    });
    setSelectedAmenities([]);
    setCustomAmenity('');
    setIsEditing(true);
    setExtras([]);
    setExtraForm({ id: 0, section_key: '', title: '', description: '', sort_order: 1, active: 1 });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle nested properties
    if (name.includes('.')) {
      const parts = name.split('.');
      
      if (parts.length === 2) {
        // Handle contact and socials top-level properties
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: value
          }
        });
      } else if (parts.length === 3) {
        // Handle nested properties like socials.instagram.handle
        setFormData({
          ...formData,
          [parts[0]]: {
            ...formData[parts[0]],
            [parts[1]]: {
              ...formData[parts[0]][parts[1]],
              [parts[2]]: value
            }
          }
        });
      }
    } else {
      // Handle top-level properties
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Extras form handlers
  const handleExtraChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExtraForm({
      ...extraForm,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    });
  };

  const resetExtraForm = () => setExtraForm({ id: 0, section_key: '', title: '', description: '', sort_order: 1, active: 1 });

  const handleExtraEdit = (extra) => {
    setExtraForm({
      id: extra.id,
      section_key: extra.section_key,
      title: extra.title || '',
      description: extra.description || '',
      sort_order: extra.sort_order || 1,
      active: extra.active ?? 1
    });
    // Bring the extras form into view and focus first field
    setTimeout(() => {
      if (extrasFormRef.current) {
        try { extrasFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
      }
      if (extrasSectionKeyRef.current) {
        try { extrasSectionKeyRef.current.focus(); } catch (e) {}
      }
    }, 0);
  };

  const handleExtraSubmit = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!selectedHotel?.id) return;
    const payload = {
      ...extraForm,
      hotel_id: selectedHotel.id,
      sort_order: parseInt(extraForm.sort_order || 1)
    };
    axiosPost(
      'hotels/extras/upsert',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success) {
          resetExtraForm();
          fetchExtras(selectedHotel.id);
        }
      }
    );
  };

  const handleExtraToggle = (extra) => {
    axiosPost(
      'hotels/extras/toggle',
      { 'Content-Type': 'application/json' },
      { id: extra.id, active: extra.active === 1 ? 0 : 1 },
      (resp) => {
        if (resp?.success) fetchExtras(selectedHotel.id);
      }
    );
  };

  // Drag-and-drop ordering handlers for extras
  const onExtraDragStart = (e, index) => {
    setDragIndex(index);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    } catch (err) {}
  };

  const onExtraDragOver = (e, index) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (err) {}
  };

  const persistExtrasOrder = (ordered) => {
    if (!selectedHotel?.id) return;
    const items = ordered.map((ex) => ex.id);
    axiosPost(
      'hotels/extras/reorder',
      { 'Content-Type': 'application/json' },
      { hotel_id: selectedHotel.id, items },
      (resp) => {
        if (resp?.success) {
          fetchExtras(selectedHotel.id);
        }
      }
    );
  };

  const onExtraDrop = (e, index) => {
    e.preventDefault();
    const fromStr = e.dataTransfer.getData('text/plain');
    const from = parseInt(fromStr, 10);
    if (isNaN(from) || from === index) { setDragIndex(null); return; }
    const newOrder = Array.from(extras);
    const [moved] = newOrder.splice(from, 1);
    newOrder.splice(index, 0, moved);
    setExtras(newOrder);
    setDragIndex(null);
    persistExtrasOrder(newOrder);
  };

  const onExtraDragEnd = () => setDragIndex(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Serialize map_location to JSON string for storage
    let locationValue = '';
    if (formData.map_location && formData.map_location.lat && formData.map_location.lng) {
      locationValue = JSON.stringify(formData.map_location);
    }
    const payload = {
      id: selectedHotel?.id || 0,
      title: formData.title,
      country_id: parseInt(formData.country_id || 0),
      location: locationValue,
      address: formData.contact.address || '',
      phone: formData.contact.phone || '',
      website: formData.contact.website || '',
      email: formData.contact.email || '',
      description: formData.content || '',
      image: formData.image || '',
      images: parseImagesString(formData.images).join('\n'),
      amenities: selectedAmenities.join('\n'),
      active: formData.active ? 1 : 0,
      queer_owned: formData.queer_owned ? 1 : 0
    };
    axiosPost(
      'hotels/update',
      { 'Content-Type': 'application/json' },
      payload,
      (resp) => {
        if (resp?.success) {
          // Reload list and close form
          setIsLoading(true);
          loadHotels();
          setIsEditing(false);
        }
      }
    );
  };

  // Image upload handlers
  const handleImagesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setIsUploadingImages(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        const resp = await axios.post(
          `${config.api.url}hotels/upload-image`,
          fd,
          {
            headers: {
              'api': config.api.key,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        const url = resp?.data?.data?.url || resp?.data?.url;
        if (resp?.data?.success && url) {
          setFormData(prev => ({
            ...prev,
            images: [ ...parseImagesString(prev.images), url ].join('\n')
          }));
        }
      }
    } catch (err) {
      console.error('Image upload failed', err);
    } finally {
      setIsUploadingImages(false);
      // reset input value so same files can be re-selected if needed
      try { e.target.value = ''; } catch (ex) {}
    }
  };

  const removeImageAt = (idx) => {
    const arr = parseImagesString(formData.images);
    if (idx < 0 || idx >= arr.length) return;
    arr.splice(idx, 1);
    setImagesFromArray(arr);
  };

  const moveImage = (idx, dir) => {
    const arr = parseImagesString(formData.images);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    const temp = arr[idx];
    arr[idx] = arr[newIdx];
    arr[newIdx] = temp;
    setImagesFromArray(arr);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  // no-op

  // Filter handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setIsLoading(true);
    loadHotels();
  };

  const resetFilters = () => {
    const cleared = { country_id: '', active: '' };
    setFilters(cleared);
    setIsLoading(true);
    loadHotels(undefined, cleared);
  };

  return (
    <div>
      <div className="DashboardPanel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 className="DashboardTitle">Hotels Management</h1>
          <button className="DashboardButton" onClick={handleAddNew}>
            <FaPlus style={{ marginRight: '5px' }} /> Add New Hotel
          </button>
        </div>

        {isLoading ? (
          <p>Loading hotels...</p>
        ) : isEditing ? (
          <form onSubmit={handleSubmit} className="DashboardForm">
            <h2 className="DashboardSubtitle">
              {selectedHotel ? 'Edit Hotel' : 'Add New Hotel'}
            </h2>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Hotel Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="DashboardFormInput"
                placeholder="Enter hotel name"
                required
              />
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Country</label>
              <select
                name="country_id"
                value={formData.country_id}
                onChange={handleChange}
                className="DashboardFormInput"
                required
              >
                <option value="">Select a country</option>
                {countries.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <h3>Map Location</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Pin Location on Map</label>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '10px' }}>
                Click on the map to drop a pin at the hotel's location. You can drag the pin to adjust it.
              </p>
              <MapPicker
                value={formData.map_location}
                onChange={(loc) => setFormData(prev => ({ ...prev, map_location: loc }))}
                apiKey={config.googleMaps.apiKey}
              />
            </div>
            
            <h3>Contact Information</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Address</label>
              <input
                type="text"
                name="contact.address"
                value={formData.contact.address}
                onChange={handleChange}
                className="DashboardFormInput"
                placeholder="Enter full address"
                required
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Phone</label>
                <input
                  type="text"
                  name="contact.phone"
                  value={formData.contact.phone}
                  onChange={handleChange}
                  className="DashboardFormInput"
                  placeholder="+1 234-567-8900"
                  required
                />
              </div>
              
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Email</label>
                <input
                  type="email"
                  name="contact.email"
                  value={formData.contact.email}
                  onChange={handleChange}
                  className="DashboardFormInput"
                  placeholder="contact@hotel.com"
                  required
                />
              </div>
            </div>
            
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Website</label>
              <input
                type="url"
                name="contact.website"
                value={formData.contact.website}
                onChange={handleChange}
                className="DashboardFormInput"
                placeholder="https://www.hotel-website.com"
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Image URL</label>
                <input
                  type="url"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  className="DashboardFormInput"
                  placeholder="https://.../image.jpg"
                />
              </div>
              <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}>
                <label className="DashboardFormLabel" style={{ marginRight: 10 }}>Active</label>
                <input
                  type="checkbox"
                  name="active"
                  checked={!!formData.active}
                  onChange={handleChange}
                  style={{ transform: 'scale(1.2)' }}
                />
              </div>
            </div>

            <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}>
              <label className="DashboardFormLabel" style={{ marginRight: 10 }}>Queer Owned</label>
              <input
                type="checkbox"
                name="queer_owned"
                checked={!!formData.queer_owned}
                onChange={handleChange}
                style={{ transform: 'scale(1.2)' }}
              />
            </div>

            <h3>Images</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Upload Images</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImagesSelected}
                disabled={isUploadingImages}
              />
              {isUploadingImages && <div style={{ marginTop: 6, fontSize: 12, color: '#555' }}>Uploading...</div>}
            </div>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Image URLs (newline or comma separated)</label>
              <textarea
                name="images"
                value={formData.images}
                onChange={handleChange}
                className="DashboardFormInput"
                placeholder={'https://.../image1.jpg\nhttps://.../image2.jpg'}
                rows="4"
              />
            </div>
            {parseImagesString(formData.images).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <label className="DashboardFormLabel" style={{ display: 'block', marginBottom: 6 }}>Preview & Order</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, alignItems: 'stretch' }}>
                  {parseImagesString(formData.images).map((url, idx) => (
                    <div key={url + idx} style={{ border: '1px solid #eee', borderRadius: 6, padding: 8, overflow: 'hidden' }}>
                      <div style={{ width: '100%', height: 90, background: `#f6f6f6 url(${url}) center/cover no-repeat`, borderRadius: 4 }} />
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <button type="button" className="DashboardButtonSecondary" onClick={() => moveImage(idx, -1)} disabled={idx === 0}>Up</button>
                        <button type="button" className="DashboardButtonSecondary" onClick={() => moveImage(idx, +1)} disabled={idx === parseImagesString(formData.images).length - 1}>Down</button>
                        <button type="button" className="DashboardButtonSecondary" onClick={() => removeImageAt(idx)}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h3>Amenities</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Select Amenities</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
                {DEFAULT_AMENITIES.map(opt => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={selectedAmenities.includes(opt)}
                      onChange={() => toggleAmenity(opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>

              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={customAmenity}
                  onChange={(e) => setCustomAmenity(e.target.value)}
                  className="DashboardFormInput"
                  placeholder="Add custom amenity"
                  style={{ flex: 1 }}
                />
                <button type="button" className="DashboardButtonSecondary" onClick={addCustomAmenity}>Add</button>
              </div>

              {selectedAmenities.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {selectedAmenities.map(am => (
                    <span key={am} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f2f4f7', borderRadius: 16, padding: '4px 10px' }}>
                      {am}
                      <button type="button" onClick={() => removeAmenity(am)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#777' }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <h3>Content</h3>
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Hotel Description (HTML supported)</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                className="DashboardFormInput"
                placeholder="<bold>Overview</bold> <br/><br/>Hotel description here..."
                rows="6"
                required
              />
            </div>

            <h3>Extras</h3>
            {selectedHotel?.id ? (
              <div style={{ marginBottom: 20 }}>
                <table className="DashboardTable" style={{ marginBottom: 15 }}>
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>Title</th>
                      <th>Active</th>
                      <th>Sort</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extras.map((ex, idx) => (
                      <tr
                        key={ex.id}
                        draggable
                        onDragStart={(e) => onExtraDragStart(e, idx)}
                        onDragOver={(e) => onExtraDragOver(e, idx)}
                        onDrop={(e) => onExtraDrop(e, idx)}
                        onDragEnd={onExtraDragEnd}
                        style={{ cursor: 'move', background: dragIndex === idx ? 'rgba(0,0,0,0.05)' : undefined }}
                      >
                        <td>{ex.section_key}</td>
                        <td>{ex.title}</td>
                        <td>{ex.active ? 'Yes' : 'No'}</td>
                        <td>{ex.sort_order}</td>
                        <td>
                          <button type="button" onClick={() => handleExtraEdit(ex)} className="DashboardButtonSecondary" style={{ marginRight: 8 }}>Edit</button>
                          <button type="button" onClick={() => handleExtraToggle(ex)} className="DashboardButtonSecondary">{ex.active ? 'Disable' : 'Enable'}</button>
                        </td>
                      </tr>
                    ))}
                    {extras.length === 0 && (
                      <tr><td colSpan="5" style={{ textAlign: 'center' }}>No extras yet.</td></tr>
                    )}
                  </tbody>
                </table>
                <div ref={extrasFormRef} className="DashboardForm" style={{ padding: 0 }}>
                  {extraForm.id ? (
                    <div className="DashboardNotice" style={{ marginBottom: 10 }}>
                      Editing extra (ID: {extraForm.id}) — update fields below and click "Update Extra" or "Cancel Edit" to stop editing.
                    </div>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    <div className="DashboardFormGroup">
                      <label className="DashboardFormLabel">Section Key</label>
                      <input ref={extrasSectionKeyRef} type="text" name="section_key" value={extraForm.section_key} onChange={handleExtraChange} className="DashboardFormInput" placeholder="e.g., overview, amenities, policies" />
                    </div>
                    <div className="DashboardFormGroup">
                      <label className="DashboardFormLabel">Title</label>
                      <input type="text" name="title" value={extraForm.title} onChange={handleExtraChange} className="DashboardFormInput" placeholder="Optional title" />
                    </div>
                  </div>
                  <div className="DashboardFormGroup">
                    <label className="DashboardFormLabel">Description (HTML allowed)</label>
                    <textarea name="description" value={extraForm.description} onChange={handleExtraChange} className="DashboardFormInput" rows="4" placeholder="Details..."></textarea>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
                    <div className="DashboardFormGroup">
                      <label className="DashboardFormLabel">Sort Order</label>
                      <input type="number" name="sort_order" value={extraForm.sort_order} onChange={handleExtraChange} className="DashboardFormInput" />
                    </div>
                    <div className="DashboardFormGroup" style={{ display: 'flex', alignItems: 'center' }}>
                      <label className="DashboardFormLabel" style={{ marginRight: 10 }}>Active</label>
                      <input type="checkbox" name="active" checked={!!extraForm.active} onChange={handleExtraChange} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    {extraForm.id ? (
                      <button type="button" className="DashboardButtonSecondary" onClick={resetExtraForm}>Cancel Edit</button>
                    ) : null}
                    <button type="button" onClick={handleExtraSubmit} className="DashboardButton">{extraForm.id ? 'Update Extra' : 'Add Extra'}</button>
                  </div>
                </div>
              </div>
            ) : (
              <p>Save the hotel first to manage extras.</p>
            )}
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button type="button" className="DashboardButtonSecondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="DashboardButton">
                {selectedHotel ? 'Update Hotel' : 'Add Hotel'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 15 }}>
              <div>
                <label className="DashboardFormLabel" style={{ display: 'block', marginBottom: 4 }}>Filter by Country</label>
                <select
                  name="country_id"
                  value={filters.country_id}
                  onChange={handleFilterChange}
                  className="DashboardFormInput"
                  style={{ minWidth: 220 }}
                >
                  <option value="">All Countries</option>
                  {countries.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="DashboardFormLabel" style={{ display: 'block', marginBottom: 4 }}>Status</label>
                <select
                  name="active"
                  value={filters.active}
                  onChange={handleFilterChange}
                  className="DashboardFormInput"
                  style={{ minWidth: 160 }}
                >
                  <option value="">All</option>
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
              </div>
              <div style={{ marginTop: 22, display: 'flex', gap: 10 }}>
                <button className="DashboardButton" onClick={applyFilters} type="button">Apply Filters</button>
                <button className="DashboardButtonSecondary" onClick={resetFilters} type="button">Reset</button>
              </div>
            </div>

            <table className="DashboardTable">
            <thead>
              <tr>
                <th>Title</th>
                <th>Country</th>
                <th>Location</th>
                <th>Address</th>
                <th>Contact</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hotels.map(hotel => (
                <tr key={hotel.id}>
                  <td>{hotel.title}</td>
                  <td>{hotel.country}</td>
                  <td>{hotel.location || '-'}</td>
                  <td>{hotel.contact.address}</td>
                  <td>
                    <div>{hotel.contact.phone}</div>
                    <div>{hotel.contact.email}</div>
                  </td>
                  <td>{hotel.active ? 'Yes' : 'No'}</td>
                  <td>
                    <button 
                      onClick={() => handleEdit(hotel)} 
                      style={{ background: 'none', cursor: 'pointer', marginRight: '10px' }}
                    >
                      <FaEdit color="#1976d2" />
                    </button>
                    <button 
                      onClick={() => handleDelete(hotel.id)} 
                      style={{ background: 'none', cursor: 'pointer' }}
                    >
                      <FaTrash color="#e65100" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
